/**
 * Inviting the second adult.
 *
 * Family Fuel is a household number, and a household number that only one
 * parent can move goes stale inside a fortnight — the other adult buys the ice
 * cream, nobody logs it, and the gauge starts lying (docs/OWN_FAMILY_ROADMAP.md
 * step 2). This screen is the whole of the fix.
 *
 * It shows a code rather than asking for an email address. Fortunaut sends no
 * mail of its own, and the two adults already have a channel — which, given
 * separated households were a stated threat-model case, is not ours to assume
 * or to route through. Handing over a link is the same security property as the
 * magic link they will sign in with: possession is the authorization.
 *
 * The live invite is therefore shown, not re-minted. A screen that cut a fresh
 * code on every visit would leave a trail of live keys behind a parent who
 * simply came back to re-read the one they already sent.
 */

import { useCallback, useEffect, useState } from 'react';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { inviteLink } from '../lib/invite.js';

function expiryLabel(iso) {
  const days = Math.round((new Date(iso) - Date.now()) / 86400000);
  if (days <= 0) return 'expires today';
  return `expires in ${days} ${days === 1 ? 'day' : 'days'}`;
}

export default function Invite() {
  const { family, createInvite, listInvites, revokeInvite } = useFamily();
  const [invites, setInvites] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  // Keyed on the family id rather than on `listInvites` itself: the context
  // rebuilds its callbacks whenever anything in it changes — a child added, a
  // profile switched — and depending on their identity would refetch for
  // reasons that have nothing to do with invites.
  const load = useCallback(async () => {
    try { setInvites(await listInvites()); } catch (e) { setError(e.message); setInvites([]); }
  }, [family?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function create() {
    setBusy(true); setError(null);
    try { await createInvite(); await load(); } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function revoke(id) {
    setBusy(true); setError(null);
    try { await revokeInvite(id); setInvites((list) => list.filter((i) => i.id !== id)); }
    catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function copy(link, id) {
    // The link is on screen and selectable regardless — clipboard access is a
    // shortcut, not the only way out of this screen, so a browser that refuses
    // it costs nothing.
    try {
      await navigator.clipboard.writeText(link);
      setCopied(id);
    } catch {
      setCopied(null);
      setError('Copying was blocked — select the link and copy it by hand.');
    }
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to="/family">Family</BackLink>
        <h1>Invite another parent</h1>
      </div>

      <p className="muted">
        They sign in with their own email and see the same gauge, the same tiles, and the
        same history. Grown-ups here are peers — there is no junior parent.
      </p>

      {error && <p className="notice error">{error}</p>}

      {invites === null && <p className="muted" role="status">Loading…</p>}

      {invites?.length === 0 && (
        <div className="card stack">
          <h2>No invite out</h2>
          <p className="muted">
            One link, good for seven days, and it stops working the moment they use it.
          </p>
          <button className="primary" onClick={create} disabled={busy}>
            {busy ? 'Creating…' : 'Create an invite link'}
          </button>
        </div>
      )}

      {invites?.map((invite) => {
        const link = inviteLink(invite.code);
        return (
          <div key={invite.id} className="card stack">
            <div className="spread">
              <h2>Send them this link</h2>
              <span className="small muted">{expiryLabel(invite.expires_at)}</span>
            </div>
            <label>
              <span className="visually-hidden">Invite link</span>
              <input readOnly value={link} onFocus={(e) => e.target.select()} />
            </label>
            <div className="row">
              <button className="primary" onClick={() => copy(link, invite.id)}>
                {copied === invite.id ? 'Copied' : 'Copy link'}
              </button>
              <button className="quiet" onClick={() => revoke(invite.id)} disabled={busy}>
                Revoke
              </button>
            </div>
            <p className="small muted">
              Anyone holding this link can join your family, so send it the way you would
              send a house key. Revoke it if it goes to the wrong window — you can always
              create another.
            </p>
          </div>
        );
      })}

      {invites?.length > 0 && (
        <button className="quiet" onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create another invite'}
        </button>
      )}
    </main>
  );
}
